import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useProjects, useProjectTicketContextQuery } from "@/hooks/use-app-queries";
import { ProjectContextSummary } from "@/components/projects/ProjectContextSummary";
import type { ApiTicketType } from "@/lib/tickets";

type GuidedTicketStartProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;
};

const typeToQueryValue: Record<ApiTicketType, "estimate" | "change_order" | "extra_work"> = {
  TM_TICKET: "estimate",
  CHANGE_ORDER: "change_order",
  EXTRA_WORK: "extra_work",
};

export function GuidedTicketStart({ open, onOpenChange }: GuidedTicketStartProps) {
  const navigate = useNavigate();
  const projectsQuery = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const activeProjects = useMemo(
    () => (projectsQuery.data ?? []).filter((project) => project.status === "ACTIVE" && project.isActive),
    [projectsQuery.data],
  );
  const selectedProject = useMemo(
    () => activeProjects.find((project) => project.id === selectedProjectId) ?? null,
    [activeProjects, selectedProjectId],
  );
  const contextQuery = useProjectTicketContextQuery(selectedProjectId);

  const goToCreate = (type: ApiTicketType) => {
    if (!selectedProjectId) return;
    navigate(`/tickets/new?projectId=${encodeURIComponent(selectedProjectId)}&type=${typeToQueryValue[type]}`);
    onOpenChange(false);
  };

  const continueDraft = (ticketId: string) => {
    navigate(`/tickets/new?edit=${encodeURIComponent(ticketId)}`);
    onOpenChange(false);
  };

  const projectHasEstimate = Boolean(contextQuery.data?.hasEstimate);
  const draftByType = contextQuery.data?.draftByType ?? {};
  const showProjectContext = Boolean(selectedProject);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-slate-200/90 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.14)] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-[#0C1F4F]">Start New Ticket</DialogTitle>
          <DialogDescription className="text-[#51698F]">
            Only active projects can receive new tickets. If the project you need is missing here, reactivate it first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[#0C1F4F]">Select project</Label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-background px-3 text-sm text-[#0C1F4F]"
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
            >
              <option value="">Choose a project</option>
              {activeProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              showProjectContext
                ? "max-h-[920px] translate-y-0 opacity-100"
                : "max-h-0 -translate-y-2 opacity-0"
            }`}
          >
            <div className="pt-1">
              {selectedProject ? (
                <ProjectContextSummary
                  compact
                  project={selectedProject}
                  context={contextQuery.data}
                />
              ) : null}
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 delay-75 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              showProjectContext
                ? "max-h-[720px] translate-y-0 opacity-100"
                : "max-h-0 -translate-y-2 opacity-0"
            }`}
          >
            <div className="pt-1">
              {selectedProject ? (
                <div className="rounded-2xl border border-slate-200/90 bg-white p-4 space-y-4 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
                  <div className="space-y-1">
                    <h3 className="text-base font-semibold text-[#0C1F4F]">Choose action</h3>
                    {projectHasEstimate ? (
                      <p className="text-sm text-amber-700">You already have an Estimate for this project.</p>
                    ) : (
                      <p className="text-sm text-slate-600">Create the first estimate or add work on top of the original estimate.</p>
                    )}
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <Button
                      className="justify-center text-center"
                      variant={projectHasEstimate ? "outline" : "default"}
                      onClick={() => {
                        if (projectHasEstimate && !window.confirm("An Estimate already exists for this project. Create a new Estimate anyway?")) {
                          return;
                        }
                        goToCreate("TM_TICKET");
                      }}
                    >
                      {projectHasEstimate ? "Create new Estimate anyway" : "Create Estimate"}
                    </Button>
                    <Button className="justify-center text-center text-[#0C1F4F]" variant="outline" onClick={() => goToCreate("CHANGE_ORDER")}>
                      Create Change Order
                    </Button>
                    <Button className="justify-center text-center text-[#0C1F4F]" variant="outline" onClick={() => goToCreate("EXTRA_WORK")}>
                      Create Extra Work
                    </Button>
                  </div>

                  {draftByType.CHANGE_ORDER ? (
                    <Button variant="ghost" size="sm" className="justify-center text-center text-[#0C1F4F]" onClick={() => continueDraft(draftByType.CHANGE_ORDER!.id)}>
                      You have a draft Change Order. Continue?
                    </Button>
                  ) : null}

                  {draftByType.EXTRA_WORK ? (
                    <Button variant="ghost" size="sm" className="justify-center text-center text-[#0C1F4F]" onClick={() => continueDraft(draftByType.EXTRA_WORK!.id)}>
                      You have a draft Extra Work. Continue?
                    </Button>
                  ) : null}

                  {draftByType.TM_TICKET ? (
                    <Button variant="ghost" size="sm" className="justify-center text-center text-[#0C1F4F]" onClick={() => continueDraft(draftByType.TM_TICKET!.id)}>
                      You have a draft Estimate. Continue?
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
